/**
 * seasonFilter.js
 *
 * 「ユーザーRole再設計」 Ph.4 で追加された共通ヘルパー。
 *
 * 目的:
 *   - role 7 (企業管理者シーズン指定) ユーザーがアクセスする全てのシーズン選択 UI で、
 *     User.accessible_survey_seasons に含まれるシーズンのみを表示する。
 *   - 同時に従来の survey_code フィルタ (例: ['30010', '90301']) も適用可能。
 *
 * 使い方:
 *   const filtered = filterAccessibleSeasons(
 *     sgi.survey_season,   // group.survey_season の埋め込み配列
 *     usr.userData,        // redux store の user.userData
 *     ['30010', '90301']   // 任意の survey_code 絞り込み
 *   );
 */

/**
 * シーズン配列を「ユーザーのアクセス権 + survey_code フィルタ」 で絞り込む。
 *
 * 動作:
 * - role が 7 以外: survey_code フィルタのみ適用（従来動作と互換）
 * - role 7 + 付与シーズン 1+ 件: survey_code フィルタに加え、
 *   userData.accessibleSurveySeasons の ID 集合に含まれるシーズンだけを残す
 * - role 7 + 付与シーズン 0 件: シーズン絞り込みを行わない（フォールバック: role 3 と同等）
 *
 * @param {Array} seasons - group.survey_season の埋め込み配列 (各要素に _id, survey_code を持つ)
 * @param {Object} userData - usr.userData (role と accessibleSurveySeasons を参照)
 * @param {Array<string>} [surveyCodeFilter] - 追加で survey_code を絞り込む場合に指定
 * @returns {Array} 絞り込み後のシーズン配列 (順序保持)
 */
export function filterAccessibleSeasons(seasons, userData, surveyCodeFilter) {
  if (!Array.isArray(seasons)) return [];
  let filtered = seasons;
  //survey_code 絞り込み (従来の filter ロジックを継承)
  if (Array.isArray(surveyCodeFilter) && surveyCodeFilter.length > 0) {
    filtered = filtered.filter((s) => surveyCodeFilter.includes(s.survey_code));
  }
  //role 7 (企業管理者シーズン指定) のシーズン制限
  //付与シーズン 0 件はフォールバック (role 3 同等) で絞り込みを行わない
  if (userData && userData.role === 7) {
    const allowed = userData.accessibleSurveySeasons || [];
    if (allowed.length > 0) {
      const allowedSet = new Set(allowed.map((id) => String(id)));
      filtered = filtered.filter((s) => allowedSet.has(String(s._id)));
    }
  }
  return filtered;
}

/**
 * シーズン選択肢が 0 件のときに表示する案内文。
 * - role 7 の場合は「シーズン未付与」 を運用者に伝える文言
 * - その他の role はサーベイ未登録一般の文言
 *
 * @param {Object} userData - usr.userData
 * @returns {string}
 */
export function noAccessibleSeasonMessage(userData) {
  if (userData && userData.role === 7) {
    return '閲覧可能なシーズンが指定されていません。管理者にお問い合わせください。';
  }
  return '対象のシーズンがありません。';
}

/**
 * SurveyToken 配列を role 7 のシーズンアクセス権限で絞り込む（Ph.5 で追加）。
 *
 * 動作:
 * - role 7 以外: 元の配列をそのまま返す（従来動作と互換）
 * - role 7 + 付与シーズン 0 件: 元の配列をそのまま返す（フォールバック: role 3 と同等）
 * - role 7 + 付与シーズン 1+ 件: accessibleSurveySeasons に含まれる survey_season_id を持つトークンのみ残す
 *   （`survey_season_id` を持たない旧形式トークンは除外）
 *
 * 用途: 「受講者情報」 (CorpMgrPage) の「チケット詳細」 モーダルや、
 * 同モーダルから起動される「サーベイ結果確認」 ボタンの露出を、role 7 ユーザーに
 * 付与されたシーズンのトークンのみに限定するため。
 *
 * @param {Array} tokens - SurveyToken 配列（survey_season_id を持つ要素）
 * @param {Object} userData - usr.userData (role と accessibleSurveySeasons を参照)
 * @returns {Array} 絞り込み後のトークン配列（順序保持）
 */
export function filterAccessibleTokens(tokens, userData) {
  if (!Array.isArray(tokens)) return [];
  if (!userData || userData.role !== 7) return tokens;
  const allowed = userData.accessibleSurveySeasons || [];
  if (allowed.length === 0) return tokens;
  const allowedSet = new Set(allowed.map((id) => String(id)));
  return tokens.filter(
    (t) =>
      t &&
      t.survey_season_id &&
      allowedSet.has(String(t.survey_season_id))
  );
}

/**
 * ユーザーが「役 7 のシーズン権限内のトークン」 を少なくとも 1 件持つかを判定（Ph.5 で追加）。
 *
 * 判定ルール:
 *   - role 7 以外: 常に true（フィルタ非適用、従来動作と互換）
 *   - role 7 + 付与シーズン 0 件: 常に true（フォールバック: 全ユーザー表示）
 *   - role 7 + 付与シーズン 1+ 件: user.survey_token のいずれかが許可シーズンと一致する場合のみ true
 *
 * 用途: 「受講者情報」 (CorpMgrPage) のユーザー一覧テーブルなど、
 * 「自分が閲覧できるシーズンの受講者だけを表示する」 シーンで使用。
 *
 * @param {Object} user - User オブジェクト（survey_token 配列を持つ）
 * @param {Object} userData - usr.userData (role と accessibleSurveySeasons を参照)
 * @returns {boolean}
 */
export function userHasAccessibleSeasonToken(user, userData) {
  if (!userData || userData.role !== 7) return true;
  const allowed = userData.accessibleSurveySeasons || [];
  //シーズン付与 0 件は fallback: 全ユーザー表示（role 3 と同等）
  if (allowed.length === 0) return true;
  const allowedSet = new Set(allowed.map((id) => String(id)));
  return (
    Array.isArray(user && user.survey_token) &&
    user.survey_token.some(
      (t) =>
        t && t.survey_season_id && allowedSet.has(String(t.survey_season_id))
    )
  );
}
